/* src/components/journal/AddJournalEntryForm.js */
import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react'; // Added useEffect for logging
import { useProfile } from '../../contexts/ProfileContext';
import TagInput from './TagInput';

// Wrap with forwardRef to allow parent (modal) to interact with the form
const AddJournalEntryForm = forwardRef(({ onSaveSuccess }, ref) => {
  // --- VVVV LOG THE DESTRUCTURED FUNCTION HERE VVVV ---
  const { activeProfile, addJournalEntry, addJournalTag } = useProfile();
  // Use useEffect to log after initial render and if the function reference changes
  useEffect(() => {
    console.log("[AddJournalEntryForm - useEffect] Value of addJournalEntry from useProfile:", addJournalEntry);
    console.log("[AddJournalEntryForm - useEffect] Type of addJournalEntry:", typeof addJournalEntry);
  }, [addJournalEntry]); // Log when addJournalEntry changes
  // --- ^^^^ END LOGGING ^^^^ ---

  const [content, setContent] = useState('');
  const [entryTags, setEntryTags] = useState([]);
  const [tagInputValue, setTagInputValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  // Expose internal state or methods to parent if necessary
  useImperativeHandle(ref, () => ({
    isSaving,
    canSubmit: content.trim().length > 0,
    resetForm: () => {
      setContent('');
      setEntryTags([]);
      setTagInputValue('');
      setError('');
    }
  }));

  // Handler for confirmed tags array
  const handleConfirmedTagsChange = (newTags) => {
    setEntryTags(newTags);
  };

  // Handler for the tag input field's value change
  const handleTagInputChange = (newValue) => {
    setTagInputValue(newValue);
  };

  // Handles form submission
  const handleSubmit = async (e) => {
    console.log("[AddJournalEntryForm] handleSubmit triggered.");
    e.preventDefault();
    setError('');

    console.log("[AddJournalEntryForm] State before validation:", {
        content: content,
        isSaving: isSaving,
        activeProfileExists: !!activeProfile,
        activeProfileId: activeProfile?.id
    });

    // --- VVVV LOG BEFORE VALIDATION CHECK VVVV ---
    console.log("[AddJournalEntryForm] Checking validation. Typeof addJournalEntry:", typeof addJournalEntry);
    // --- ^^^^ END LOGGING ^^^^ ---

    if (!content.trim() || !activeProfile || isSaving || typeof addJournalEntry !== 'function') { // Added check for function type
      console.warn("[AddJournalEntryForm] handleSubmit: Exiting early due to validation or missing function.", {
          contentTrimmed: content.trim(),
          activeProfileExists: !!activeProfile,
          isSaving: isSaving,
          isAddJournalEntryFunction: typeof addJournalEntry === 'function' // Log if it's a function
      });
      // Set error if the function is missing
      if (typeof addJournalEntry !== 'function') {
          setError("Error: Save action is unavailable. Please refresh.");
      }
      return;
    }
    console.log("[AddJournalEntryForm] Passed validation. Setting isSaving=true.");
    setIsSaving(true);

    // Process unconfirmed tag if it exists
    const finalRawTags = [...entryTags];
    const unconfirmedTag = tagInputValue.trim();

    if (unconfirmedTag && !entryTags.some(t => t.toLowerCase() === unconfirmedTag.toLowerCase())) {
      finalRawTags.push(unconfirmedTag);
    }
    console.log("[AddJournalEntryForm] Final raw tags before processing:", finalRawTags);

    // Sanitize and add new global tags
    const sanitizedTags = [];
    try {
      console.log("[AddJournalEntryForm] Starting tag processing loop...");
      for (const rawTag of finalRawTags) {
        console.log(`[AddJournalEntryForm] Processing raw tag: "${rawTag}"`);
        const addedTag = await addJournalTag(activeProfile.id, rawTag); // Assume addJournalTag is okay for now
        if (addedTag && typeof addedTag === 'string') {
          sanitizedTags.push(addedTag);
        } else if (typeof rawTag === 'string' && rawTag.startsWith('#')) {
          sanitizedTags.push(rawTag);
           console.log(`[AddJournalEntryForm] Kept original tag "${rawTag}" as fallback.`);
        } else {
             console.warn(`[AddJournalEntryForm] Discarded invalid/failed tag: "${rawTag}"`);
        }
      }
      console.log("[AddJournalEntryForm] Tag processing complete. Sanitized tags:", sanitizedTags);
    } catch (tagError) {
      console.error("[AddJournalEntryForm] Error during tag processing loop:", tagError);
      setError("Error processing tags. Please try again.");
      setIsSaving(false);
      return;
    }

    // Ensure final uniqueness
    const uniqueSanitizedTags = [...new Set(sanitizedTags)];
    console.log("[AddJournalEntryForm] Unique sanitized tags:", uniqueSanitizedTags);

    // Prepare entry data
    const entryData = {
      type: 'manual',
      content: content.trim(),
      tags: uniqueSanitizedTags,
    };
    console.log("[AddJournalEntryForm] Prepared entryData:", JSON.stringify(entryData, null, 2));

    // Add the entry using the context action
    try {
      // --- VVVV LOG RIGHT BEFORE CALLING VVVV ---
      console.log("[AddJournalEntryForm] Double checking typeof addJournalEntry right before call:", typeof addJournalEntry);
      // --- ^^^^ END LOGGING ^^^^ ---
      console.log(`%c[AddJournalEntryForm] Calling addJournalEntry for profile ${activeProfile.id}...`, 'color: orange; font-weight: bold;');
      const successResult = await addJournalEntry(activeProfile.id, entryData);
      console.log("[AddJournalEntryForm] addJournalEntry returned:", successResult); // Will likely still be null

      if (successResult && successResult.id) {
         console.log("[AddJournalEntryForm] Save successful. Resetting form.");
        setContent('');
        setEntryTags([]);
        setTagInputValue('');
        setError('');
        if (onSaveSuccess) onSaveSuccess();
      } else {
        console.error("[AddJournalEntryForm] addJournalEntry call did not return a successful result object (or function was not called).");
        // Keep previous error or set a generic one if needed
        if (!error) setError('Failed to save journal entry. Please try again. (Context/Function Error)');
      }
    } catch (saveError) {
      console.error("[AddJournalEntryForm] Error calling or awaiting addJournalEntry:", saveError);
      // It might throw here if addJournalEntry is not a function
      setError('An unexpected error occurred while attempting to save.');
    } finally {
       console.log("[AddJournalEntryForm] Setting isSaving=false in finally block.");
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} id="add-journal-form" className="space-y-4">
      {/* ... rest of the form ... */}
       {/* Entry Content Textarea */}
       <div>
         <label htmlFor="journalContent" className="block text-sm font-medium text-gray-700 mb-1">
           Entry Content
         </label>
         <textarea
           id="journalContent"
           value={content}
           onChange={(e) => setContent(e.target.value)}
           placeholder="How was your training today? Any thoughts or observations?"
           rows="5"
           required
           className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500
                     focus:border-indigo-500 sm:text-sm disabled:opacity-70 disabled:bg-gray-50"
           disabled={isSaving}
         />
       </div>

       {/* Tags Section */}
       <div>
         <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
         <TagInput
           tags={entryTags}
           availableTags={activeProfile?.journalTags || []}
           onChange={handleConfirmedTagsChange}
           inputValue={tagInputValue}
           onInputChange={handleTagInputChange}
           disabled={isSaving}
         />
         <p className="text-xs text-gray-500 mt-1">
           Enter tags separated by comma or Enter. Max 25 chars, starts with #.
         </p>
       </div>

       {/* Display local form error message */}
       {error && (
         <div className="p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm" role="alert">
           <p>{error}</p>
         </div>
       )}
    </form>
  );
});

export default AddJournalEntryForm;